import React from "react";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Grid2 as Grid,
	Typography,
	MenuItem,
	TextField,
	IconButton,
	Divider,
} from "@mui/material";
import { Alert, AlertTitle } from "@mui/material";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import CloseIcon from "@mui/icons-material/Close";
import CreditRequestLineItemRow from "./CreditRequestLineItemRow";
import { LargeActionButton } from "../Common/LargeActionButton";
import { LargeActionLink } from "../Common/LargeActionLink";
import { fullScreenSecondaryModalActionStyles, primaryModalActionStyles } from "../Common/modalActionStyles";
import PageContext from "../Common/PageContext";
import { useStyles } from "./CreditRequestDialog.styles";
import { CreditRequestDialogProps } from "./types";

export default function CreditRequestDialog({
	isOpen,
	cancelButtonName,
	continueButtonName,
	onCancelButtonClick,
	onContinueButtonClick,
	isContinueButtonDisabled,
	creditRequestLineItems,
	creditRequestReasons,
	isInErrorState,
	titleMessage,
	updateLineItem,
	copyLineItemDetailsToAllLines = () => {},
	showCopyToAllButton,
	courier,
	onCourierChange = () => {},
	forceCancelReason,
	headingText,
}: CreditRequestDialogProps) {
	const classes = useStyles();

	const [pageContext] = React.useContext(PageContext);
	const { carriers: couriers } = pageContext;

	return (
		<Dialog
			open={isOpen}
			onClose={onCancelButtonClick}
			fullScreen
			scroll="body"
			aria-labelledby="credit-request-dialog-title"
		>
			<DialogTitle>
				<Grid container justifyContent="space-between" alignItems="center" wrap="nowrap">
					<Grid>
						<Typography id="credit-request-dialog-title" variant="h5" className={classes.heading}>
							{headingText}
						</Typography>
					</Grid>
					<Grid>
						<IconButton onClick={onCancelButtonClick} aria-label="close">
							<CloseIcon className={classes.closeIcon} />
						</IconButton>
					</Grid>
				</Grid>
				<Grid container spacing={1} wrap="nowrap" alignItems="flex-start" className={classes.warningRow}>
					<Grid>
						<ErrorOutlineRoundedIcon className={classes.warningColor} />
					</Grid>
					<Grid size="grow">
						<Typography variant="body1" display="block" className={classes.warningText}>
							{titleMessage}
						</Typography>
					</Grid>
				</Grid>
			</DialogTitle>
			<DialogContent>
				{isInErrorState ? (
					<Alert severity="error">
						<AlertTitle>Error</AlertTitle>
						An error occurred, please contact IT support if issues persist.
					</Alert>
				) : (
					<React.Fragment>
						{!forceCancelReason && (
							<Grid container justifyContent="center" className={classes.courierRow}>
								<Grid size={12}>
									<TextField
										id="courier"
										select
										label="Courier"
										variant="outlined"
										size="small"
										fullWidth
										value={courier ?? ""}
										onChange={onCourierChange}
										className={classes.courierField}
										slotProps={{
											inputLabel: { shrink: Boolean(courier) },
										}}
									>
										{couriers.map((courierOption) => (
											<MenuItem key={courierOption} value={courierOption}>
												{courierOption}
											</MenuItem>
										))}
									</TextField>
								</Grid>
							</Grid>
						)}
						<Grid container justifyContent="center" spacing={0}>
							{creditRequestLineItems.map((row, index) => (
								<React.Fragment key={row.id ?? row.sku}>
									{index > 0 && (
										<Grid size={12}>
											<Divider />
										</Grid>
									)}
									<CreditRequestLineItemRow
										creditRequestLineItem={row}
										creditRequestReasons={creditRequestReasons}
										updateLineItem={updateLineItem}
										copyLineItemDetailsToAllLines={copyLineItemDetailsToAllLines}
										showCopyToAll={showCopyToAllButton && index === 0}
										hideCourier
										forceCancelReason={forceCancelReason}
									/>
								</React.Fragment>
							))}
						</Grid>
					</React.Fragment>
				)}
			</DialogContent>
			<DialogActions>
				<Grid container direction="column" spacing={1} sx={{ width: "100%", px: 2 }}>
					<Grid size={12} container justifyContent="center" alignItems="center" sx={{ width: "100%" }}>
						<LargeActionButton
							onClick={onContinueButtonClick}
							disabled={isContinueButtonDisabled}
							sx={primaryModalActionStyles}
							children={continueButtonName}
						/>
					</Grid>
					<Grid size={12} container justifyContent="center" alignItems="center" sx={{ width: "100%" }}>
						<LargeActionLink
							onClick={onCancelButtonClick}
							sx={fullScreenSecondaryModalActionStyles}
							children={cancelButtonName}
						/>
					</Grid>
				</Grid>
			</DialogActions>
		</Dialog>
	);
}
